import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://www.veed.io/pricing";
const ARTICLE = "https://www.veed.io/learn/veed-vs-capcut";
const CHECKED = "2026-10-09";

export const VEED: CompetitorRecord = {
  slug: "veed",
  name: "VEED",
  aliases: ["VEED.IO", "Veed", "veed.io"],
  url: "https://www.veed.io",
  pricingUrl: PRICING,
  category: "editor",
  positioning: "Browser video suite for teams with subtitles, avatars and AI video generation.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "10 minutes of video a month",
      includes: [
        "720p export",
        "Watermark on exports",
        "2 minutes of auto-subtitles",
        "Timeline editor",
      ],
      sourceUrl: ARTICLE,
      checkedAt: CHECKED,
      note: "Limits from VEED's own veed-vs-capcut article; the live pricing page offers Start for free.",
    },
    {
      name: "Creator",
      monthly: 29,
      annualPerMonth: 14,
      annualTotal: 171,
      currency: "CAD",
      metered: "500 AI credits a month on monthly billing, 6,000 a year on yearly billing",
      includes: [
        "About 1,500 AI videos per year of credits",
        "No watermark",
        "Unlimited auto subtitles",
        "Priced per user",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Rendered in Canadian dollars on Oct 9, 2026; the USD list price was not displayed.",
    },
    {
      name: "Pro",
      monthly: 39,
      annualPerMonth: 19,
      annualTotal: 229,
      currency: "CAD",
      metered: "2,500 AI credits a month on monthly billing, 30,000 a year on yearly billing",
      includes: [
        "Multiple brand kits",
        "Translate to 50+ languages",
        "Up to 144 hours a year of AI voice",
        "4K export and 120-minute uploads, per VEED's article",
        "Priced per user",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Rendered in Canadian dollars on Oct 9, 2026; the USD list price was not displayed.",
    },
    {
      name: "Studio",
      monthly: 79,
      annualPerMonth: 39,
      annualTotal: 465,
      currency: "CAD",
      metered: "15,000 AI credits a month on monthly billing, 180,000 a year on yearly billing",
      includes: [
        "Custom templates",
        "Up to 960 hours a year of AI voice",
        "Priced per user",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Rendered in Canadian dollars on Oct 9, 2026; the USD list price was not displayed.",
    },
    {
      name: "Enterprise",
      custom: true,
      metered: "Custom",
      includes: ["Contact sales", "Per-seat pricing"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Creator",
  entryPlanIncludes:
    "500 AI credits a month (6,000 a year on yearly billing), about 1,500 AI videos a year, no watermark, unlimited auto subtitles",
  freeTier:
    "10 minutes of video a month at 720p with a watermark and 2 minutes of auto-subtitles, per VEED's own article",
  trial: "None advertised; the pricing page offers Start for free on the Free plan",
  meteringUnit:
    "AI credits, 500 a month on Creator monthly billing or 6,000 a year on yearly billing; the credit cost of one clip is not published",
  exportResolution:
    "720p on Free; the live page lists 1080p and 4K Ultra HD exports on paid tiers, with 4K on Pro per VEED's article",
  features: {
    longToShort: { v: "yes", note: "AI Clip Generator" },
    momentRanking: { v: "yes", note: "0 to 10 score on four factors" },
    reframing: { v: "n/a", note: "Not stated for clips" },
    maxSource: { v: "text", note: "120 minutes on Pro, per VEED's article" },
    layouts: { v: "n/a", note: "Not enumerated" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "yes" },
    voiceover: { v: "yes", note: "Metered in hours per year on paid plans" },
    voiceCloning: { v: "yes" },
    avatars: { v: "yes" },
    timelineEditor: { v: "yes", note: "Free plan too, with a watermark" },
    captionStyles: { v: "n/a", note: "Count not published" },
    captionLanguages: { v: "text", note: "50+ on VEED's own pages" },
    bRoll: { v: "yes" },
    fourK: { v: "partial", note: "Pro and up, per VEED's article" },
    scheduling: { v: "no" },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "yes", note: "Fabric and a subtitle API" },
    webhooks: { v: "n/a", note: "Not stated" },
    web: { v: "yes" },
    desktop: { v: "no" },
    mobile: { v: "partial", note: "iOS app; Android listed as in development" },
    teamSeats: { v: "yes", note: "Per-seat pricing, Teams and Enterprise" },
    freeTier: { v: "yes", note: "10 minutes a month, 720p, watermark" },
    trial: { v: "n/a", note: "Not advertised; the Free plan stands in" },
    metering: { v: "text", note: "AI credits, monthly or a year's worth up front" },
    rollover: { v: "no", note: "Yearly credits reset at renewal" },
    refunds: { v: "text", note: "FAQ entry exists; terms not captured" },
    cancelInApp: { v: "n/a", note: "Not stated on the pricing page" },
  },
  billingTerms: {
    refunds:
      "VEED's pricing FAQ has a heading titled What is your refund policy?; its text was not captured, so see VEED's FAQ at https://www.veed.io/pricing. A Capterra reviewer in May 2026 stated that downloading anything voids the 14-day refund policy (https://www.capterra.com/p/193780/VEED/reviews/?rating=2).",
    cancel: "Cancellation steps are not described on the pricing page.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "charged me for an extra year, and are refusing to refund",
      date: "2026-10-05",
      url: "https://www.trustpilot.com/review/veed.io",
      evidences: "refunds",
    },
    {
      text: "token allowance was too small to decide",
      date: "2026-09-28",
      url: "https://www.trustpilot.com/review/veed.io",
      evidences: "trial",
    },
  ],
  verification: {
    method: "browser",
    date: CHECKED,
    note: "The raw HTML of veed.io/pricing contains no prices (JavaScript-rendered). Opened in a real browser from Canada on Oct 9, 2026: Creator CA$29/mo or CA$14/mo billed yearly as CA$171, Pro CA$39/mo or CA$19/mo billed yearly as CA$229, Studio CA$79/mo or CA$39/mo billed yearly as CA$465, Enterprise custom, toggle Save up to 51%. Prices rendered in CAD and USD list prices were not displayed; a USD browser check is still needed. Free plan limits (10 minutes a month, 720p, watermark, 2 minutes of auto-subtitles) come from VEED's veed-vs-capcut article, which still describes an older Lite and Pro ladder in USD.",
  },
  seoPattern: "veed.io/learn/veed-vs-<competitor> and /alternatives hub, no page about Crayo",
};
